import { useCallback } from "react";

import { matchPath, useLocation } from "react-router-dom";

import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  CMP_ORGANISATIONS_ROOT_URL,
  CMP_DASHBOARD_URL,
  CMP_ORDERS_URL,
  CMP_ORDER_URL,
  CMP_STOCK_URL,
  CMP_BUSINESS_URL,
  CMP_COMMISSIONS_URL,
  CMP_PATIENTS_URL,
  CMP_SHOP_URL,
  CMP_SHOP_TAB_URL,
  CMP_SUPPORT_URL,
  CMP_LEARNING_HUB_URL,
  CMP_LEARNING_HUB_DASHBOARD_URL,
  CMP_LEARNING_HUB_COURSES_URL,
  CMP_LEARNING_HUB_COURSE_URL,
  CMP_LEARNING_HUB_WEBINARS_URL,
  CMP_LEARNING_HUB_WEBINAR_URL,
  CMP_LEARNING_HUB_EVENTS_URL,
  CMP_LEARNING_HUB_EVENT_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDES_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDE_URL,
  CMP_PATIENTS_BASE_URL,
  CMP_PATIENTS_PROFILE_URL,
  CMP_PATIENTS_DASHBOARD_URL,
  CMP_PATIENTS_TESTS_URL,
  CMP_PATIENTS_TEST_URL,
  CMP_PATIENTS_WELLNESS_URL,
  CMP_PATIENTS_SUBSECTOR_URL,
  CMP_PATIENTS_SELF_ASSESSMENT_URL,
  CMP_PATIENTS_SUGGESTIONS_URL,
  CMP_PATIENTS_SUGGESTION_URL,
  CMP_PATIENTS_FOOD_LIST_URL,
  CMP_PATIENTS_USER_RESULT_URL,
  CMP_PATIENTS_THEME_SUBSECTOR_URL,
  CMP_PATIENTS_USER_DATAPOINT_URL,
  CMP_PROFILE_URL,
  CMP_SIGNIN_URL,
  CMP_BUSINESS_TAB_URL,
  CMP_CHECKOUT_URL,
  CMP_AFFILIATE_LINKS,
  getCmpUrl,
  CMP_PATIENTS_MANAGE_PATIENTS_URL,
  CMP_PATIENTS_CALLBACK_REQUESTS_URL,
  CMP_PATIENTS_NEW_RESULTS_URL
} from "@/core/urls";

const URLS = [
  CMP_ORGANISATIONS_ROOT_URL,
  CMP_DASHBOARD_URL,
  CMP_ORDERS_URL,
  CMP_ORDER_URL,
  CMP_STOCK_URL,
  CMP_BUSINESS_URL,
  CMP_PATIENTS_URL,
  CMP_PATIENTS_MANAGE_PATIENTS_URL,
  CMP_PATIENTS_CALLBACK_REQUESTS_URL,
  CMP_PATIENTS_NEW_RESULTS_URL,
  CMP_COMMISSIONS_URL,
  CMP_SHOP_URL,
  CMP_SHOP_URL,
  CMP_SHOP_TAB_URL,
  CMP_SUPPORT_URL,
  CMP_LEARNING_HUB_URL,
  CMP_LEARNING_HUB_DASHBOARD_URL,
  CMP_LEARNING_HUB_COURSES_URL,
  CMP_LEARNING_HUB_COURSE_URL,
  CMP_LEARNING_HUB_WEBINARS_URL,
  CMP_LEARNING_HUB_WEBINAR_URL,
  CMP_LEARNING_HUB_EVENTS_URL,
  CMP_LEARNING_HUB_EVENT_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDES_URL,
  CMP_LEARNING_HUB_VIDEO_GUIDE_URL,
  CMP_PROFILE_URL,
  CMP_SIGNIN_URL,
  CMP_BUSINESS_TAB_URL,
  CMP_CHECKOUT_URL,
  CMP_AFFILIATE_LINKS,
  CMP_PATIENTS_BASE_URL,
  CMP_PATIENTS_PROFILE_URL,
  CMP_PATIENTS_DASHBOARD_URL,
  CMP_PATIENTS_TESTS_URL,
  CMP_PATIENTS_TEST_URL,
  CMP_PATIENTS_WELLNESS_URL,
  CMP_PATIENTS_SUBSECTOR_URL,
  CMP_PATIENTS_SELF_ASSESSMENT_URL,
  CMP_PATIENTS_SUGGESTIONS_URL,
  CMP_PATIENTS_SUGGESTION_URL,
  CMP_PATIENTS_FOOD_LIST_URL,
  CMP_PATIENTS_USER_RESULT_URL,
  CMP_PATIENTS_THEME_SUBSECTOR_URL,
  CMP_PATIENTS_USER_DATAPOINT_URL
];

export default function useCmpRelatedUrl() {
  const organisationContext = useOrganisationContext();

  /*

    It used to be the cmp related urls were like this -

        /partners/dashboard/profile
        /partners/dashboard/orders/direct

    At these urls an organisation must be selected.

    Now we want the organisation id in the url

        /partners/dashboard/profile -> /cmp/organisations/:organisationId/dashboard/profile

    Problem - sometimes we need to link to cmp pages but are outside org context.

    Solution - this hook will work out the url if the organisation is known.
    Otherwise it will fallback to a url which will take the user to the final destination
    by via redirect which first determines the organisation to use (either gotten via local storage or just the first org found from those available).

    */

  const location = useLocation();

  const resolvedUrls = {};

  const isAtUrl = useCallback(
    (_url, config = { exact: false, strict: false }) => {
      const result = !!matchPath(location.pathname, {
        path: _url,
        ...config
      });

      return [!!result, result];
    },
    [location.pathname]
  );

  if (organisationContext) {
    for (const url of URLS) {
      resolvedUrls[url] = getCmpUrl(url, {
        organisationId: organisationContext?.organisation?.hashId
      });
    }
    return { resolvedUrls, isAtUrl };
  }

  for (const url of URLS) {
    // e.g. go to /cmp/dashboard/profile
    // /cmp/dashboard determines org
    // it then redirects to /cmp/dashboard/organisation/orghashid/profile
    resolvedUrls[url] = url.replace("/organisations/:organisationId", "");
  }

  return { resolvedUrls, isAtUrl };
}
